import React, { useState, useEffect, useRef } from 'react';
import API from '../services/api';

// Helper to format dates as MM-DD-YYYY
function formatDate(isoString) {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString; // fallback if invalid
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function AdminInvoicePage() {
  const [allClients, setAllClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedClientName, setSelectedClientName] = useState('');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Split out the editable fields for easy user editing.
  const [clientName, setClientName] = useState('');
  const [diagnosisCode, setDiagnosisCode] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZipCode, setBillingZipCode] = useState(''); // Renamed to zip_code

  const [sessions, setSessions] = useState([]);
  const [invoiceDates, setInvoiceDates] = useState({ start: '', end: '' });

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);  // toggle to enable/disable editing

  // We'll store a ref to the invoice section for PDF printing
  const invoiceRef = useRef(null);

  useEffect(() => {
    fetchAllClients();
  }, []);

  // 1) Fetch all clients
  const fetchAllClients = async () => {
    try {
      const res = await API.get('/admin/all-clients');
      setAllClients(res.data);
    } catch (error) {
      console.error(error);
      alert('Error fetching all clients');
    }
  };

  // 2) Filter displayed clients
  const displayedClients = searchTerm.trim()
    ? allClients.filter(c =>
        c.client_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allClients;

  // 3) Handle selecting a client from the list
  const handleSelectClient = (client) => {
    setSelectedClientId(client.id);
    setSelectedClientName(client.client_name);
    setSearchTerm(client.client_name);
    clearInvoiceData();
  };

  // If user types in the search box again, clear out the old invoice.
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    clearInvoiceData();
    setSelectedClientId(null);
    setSelectedClientName('');
  };

  // Helper to clear the invoice data states
  const clearInvoiceData = () => {
    setClientName('');
    setDiagnosisCode('');
    setClientPhone('');
    setClientEmail('');
    setBillingAddress('');
    setBillingCity('');
    setBillingState('');
    setBillingZipCode('');
    setSessions([]);
    setInvoiceDates({ start: '', end: '' });
  };

  // 4) Fetch the invoice
  const fetchInvoice = async () => {
    if (!selectedClientId || !startDate || !endDate) {
      alert('Please select a client and date range.');
      return;
    }
    setLoading(true);
    try {
      const res = await API.get('/admin/client-invoice', {
        params: {
          clientId: selectedClientId,
          startDate,
          endDate,
        },
      });

      // Break out the returned data into states:
      // (Ensure your back-end returns city AS city, state AS state, and zip_code AS zip_code)
      setClientName(res.data.clientName || '');
      setDiagnosisCode(res.data.diagnosisCode || '');
      setClientPhone(res.data.clientPhone || '');
      setClientEmail(res.data.clientEmail || '');
      setBillingAddress(res.data.billingAddress || '');
      setBillingCity(res.data.city || '');
      setBillingState(res.data.state || '');
      setBillingZipCode(res.data.zip_code || '');
      setSessions(res.data.sessions || []);
      setInvoiceDates({
        start: res.data.startDate,
        end: res.data.endDate,
      });
    } catch (error) {
      console.error(error);
      alert('Error fetching invoice');
    } finally {
      setLoading(false);
    }
  };

  // 5) Calculate total fees
  const totalFees = sessions.reduce(
    (sum, s) => sum + (s.billed_amount || 0),
    0
  );

  // 6) Export to CSV
  const exportToCSV = () => {
    if (!sessions.length) return;

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += 'Date of Service,Service Type,Therapist,Fee,Status\n';

    sessions.forEach((s) => {
      const row = [
        formatDate(s.session_date),
        `"${s.service_type}"`,
        `"${s.therapist_name}"`,
        s.billed_amount || 0,
        s.status || '',
      ];
      csvContent += row.join(',') + '\n';
    });

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute(
      'download',
      `Invoice_${clientName.replace(/\s/g, '_')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 7) Export to PDF
  const exportToPDF = () => {
    if (!sessions.length) return;

    const newWin = window.open('', '_blank', 'width=800,height=900');
    if (!newWin) {
      alert('Failed to open new window for PDF export.');
      return;
    }
    newWin.document.write('<html><head><title>Invoice Print</title></head><body>');
    newWin.document.write(invoiceRef.current.innerHTML);
    newWin.document.write('</body></html>');
    newWin.document.close();
    newWin.focus();
    newWin.print();
    newWin.close();
  };

  // Toggle editing mode
  const toggleEditing = () => {
    setIsEditing(!isEditing);
  };

  return (
    <div style={{ margin: '2rem' }}>
      <h2>Generate Client Invoice</h2>

      {/* SEARCH + LIST WRAPPER */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
        {/* LEFT: SEARCH INPUT */}
        <div>
          <label>Search Client:</label>
          <br />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Type to search by client name..."
            style={{ width: '200px' }}
          />
          {selectedClientName && (
            <div style={{ marginTop: '0.5rem' }}>
              <strong>Selected Client:</strong> {selectedClientName}
            </div>
          )}
        </div>

        {/* RIGHT: BOX OF CLIENTS */}
        <div
          style={{
            border: '1px solid #ccc',
            width: '220px',
            height: '250px',
            overflowY: 'auto',
            padding: '0.5rem',
          }}
        >
          {displayedClients.length === 0 ? (
            <div style={{ color: '#999' }}>No clients found</div>
          ) : (
            displayedClients.map((client) => (
              <div
                key={client.id}
                style={{
                  cursor: 'pointer',
                  borderBottom: '1px solid #eee',
                  padding: '0.25rem 0',
                }}
                onClick={() => handleSelectClient(client)}
              >
                {client.client_name}
              </div>
            ))
          )}
        </div>
      </div>

      {/* DATE RANGE */}
      <div style={{ marginTop: '1rem' }}>
        <label>Start Date:</label>
        <br />
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          required
        />
      </div>
      <div style={{ marginTop: '1rem' }}>
        <label>End Date:</label>
        <br />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          required
        />
      </div>

      {/* PREVIEW BUTTON */}
      <button onClick={fetchInvoice} style={{ margin: '1rem 0' }}>
        Preview Report
      </button>
      {loading && <p>Loading...</p>}

      {sessions.length > 0 && (
        <div>
          <button onClick={toggleEditing} style={{ marginBottom: '1rem' }}>
            {isEditing ? 'Stop Editing' : 'Enable Editing'}
          </button>
        </div>
      )}

      {/* INVOICE DISPLAY */}
      {sessions.length > 0 && (
        <div
          ref={invoiceRef}
          style={{
            marginTop: '2rem',
            border: '1px solid #ccc',
            padding: '1rem',
          }}
        >
          {/* LETTERHEAD (if you have an image or HTML snippet) */}
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img
              src="/letterhead.png"
              alt="Letterhead"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>

          {/* INVOICE HEADER / CLIENT INFO FIELDS */}
          <h3>
            Invoice for{' '}
            {isEditing ? (
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
              />
            ) : (
              clientName
            )}
          </h3>

          <p>
            <strong>Diagnosis: </strong>
            {isEditing ? (
              <input
                type="text"
                value={diagnosisCode}
                onChange={(e) => setDiagnosisCode(e.target.value)}
              />
            ) : (
              diagnosisCode
            )}
          </p>

          <p>
            <strong>Phone: </strong>
            {isEditing ? (
              <input
                type="text"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
              />
            ) : (
              clientPhone
            )}
          </p>

          <p>
            <strong>Email: </strong>
            {isEditing ? (
              <input
                type="text"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
              />
            ) : (
              clientEmail
            )}
          </p>

          {/* ADDRESS + city, state, zip_code */}
          <p>
            <strong>Address: </strong>
            {isEditing ? (
              <span style={{ display: 'block' }}>
                <input
                  type="text"
                  placeholder="Street address"
                  value={billingAddress}
                  onChange={(e) => setBillingAddress(e.target.value)}
                  style={{ marginBottom: '4px', width: '100%' }}
                />
                <input
                  type="text"
                  placeholder="City"
                  value={billingCity}
                  onChange={(e) => setBillingCity(e.target.value)}
                  style={{ marginBottom: '4px', width: '100%' }}
                />
                <input
                  type="text"
                  placeholder="State"
                  value={billingState}
                  onChange={(e) => setBillingState(e.target.value)}
                  style={{ marginBottom: '4px', width: '100%' }}
                />
                <input
                  type="text"
                  placeholder="Zip"
                  value={billingZipCode}
                  onChange={(e) => setBillingZipCode(e.target.value)}
                  style={{ width: '100%' }}
                />
              </span>
            ) : (
              <>
                {billingAddress}
                {billingAddress && ', '}
                {billingCity}
                {billingCity && ', '}
                {billingState} {billingZipCode}
              </>
            )}
          </p>

          <p>
            <strong>Billing Period:</strong>{' '}
            {formatDate(invoiceDates.start)} to {formatDate(invoiceDates.end)}
          </p>

          {/* TABLE */}
          <table
            border="1"
            cellPadding="5"
            style={{ borderCollapse: 'collapse', width: '100%' }}
          >
            <thead>
              <tr>
                <th>Date of Service</th>
                <th>Service Type</th>
                <th>Therapist</th>
                <th>Fee</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center' }}>
                    No sessions found
                  </td>
                </tr>
              ) : (
                sessions.map((s, index) => (
                  <tr key={index}>
                    <td>{formatDate(s.session_date)}</td>
                    <td>{s.service_type}</td>
                    <td>{s.therapist_name}</td>
                    <td>${s.billed_amount}</td>
                    <td>{s.status || 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <h4 style={{ marginTop: '1rem' }}>
            Total Fees: <strong>${totalFees.toFixed(2)}</strong>
          </h4>

          {/* FOOTER */}
          <div style={{ marginTop: '2rem', textAlign: 'center', fontStyle: 'italic' }}>
            <hr />
            <p style={{ marginTop: '1rem' }}>
          <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
            <img
              src="/footer.png"
              alt="Footer"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>
            </p>
          </div>
        </div>
      )}

      {/* EXPORT BUTTONS */}
      {sessions.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <button onClick={exportToCSV} style={{ marginRight: '0.5rem' }}>
            Export to Excel (CSV)
          </button>
          <button onClick={exportToPDF}>Export to PDF</button>
        </div>
      )}
    </div>
  );
}

export default AdminInvoicePage;
